import * as React from "react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import { Dashboard } from "../../store/types.tsx";
import {
  conversationActions,
  deleteDashboard,
  patchDashboard,
  selectDashboardAction,
} from "../../store/conversation.tsx";

interface DashboardDrawerSectionProps {
  dashboards: Dashboard[];
  title: string;
  closeDrawer: () => void;
  onDashboardSelected?: () => void;
}

const DashboardDrawerSection: React.FC<DashboardDrawerSectionProps> = ({
  dashboards,
  title,
  closeDrawer,
  onDashboardSelected,
}) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [menuDashboard, setMenuDashboard] = React.useState<Dashboard | null>(
    null,
  );
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState("");
  const { selectedDashboard, selectedView } = useTypedSelector(
    (state) => state.conversationState,
  );

  React.useEffect(() => {
    if (selectedView === "dashboard") {
      setExpanded(true);
    }
  }, [selectedView]);

  const handleSelectDashboard = (dashboardId: string) => {
    closeDrawer();
    onDashboardSelected?.();
    dispatch(conversationActions.setSelectedView("dashboard"));
    dispatch(selectDashboardAction(dashboardId) as any);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    dashboard: Dashboard,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuDashboard(dashboard);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenRename = () => {
    if (!menuDashboard) return;
    setRenameValue(menuDashboard.title);
    setRenameOpen(true);
    handleCloseMenu();
  };

  const handleConfirmRename = async () => {
    if (!menuDashboard || renameValue.trim() === "") {
      return;
    }

    await dispatch(patchDashboard(menuDashboard.id, renameValue.trim()) as any);
    setRenameOpen(false);
    setMenuDashboard(null);
  };

  const handleOpenDelete = () => {
    setDeleteOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    if (!menuDashboard) return;

    await dispatch(deleteDashboard(menuDashboard.id) as any);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  const handleCloseDialogs = () => {
    setRenameOpen(false);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography
        variant="h4"
        onClick={() => setExpanded((current) => !current)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
          "&:hover": {
            opacity: 0.82,
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <DashboardCustomizeOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "left",
          }}
        >
          {title}
        </Box>
      </Typography>
      <Box
        sx={{
          height: "1px",
          backgroundColor: "rgba(17, 17, 17, 0.15)",
          marginBottom: expanded ? "10px" : "2px",
        }}
      />
      {expanded && (
        <Box
          sx={{
            position: "relative",
            mb: 0.5,
            ml: "16px",
            pl: "12px",
            maxHeight: "126px",
            overflowY: "auto",
            pr: "2px",
            direction: "rtl",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(17, 17, 17, 0.22) transparent",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(17, 17, 17, 0.18)",
              borderRadius: "999px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "2px",
              bottom: "2px",
              width: "1px",
              backgroundColor: "rgba(17, 17, 17, 0.14)",
            },
          }}
        >
          <Box
            sx={{
              direction: "ltr",
              width: "100%",
            }}
          >
            {dashboards.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0, 0, 0, 0.65)",
                  ml: "0px",
                  mb: 0.5,
                }}
              >
                {i18n._(t`No dashboards yet`)}
              </Typography>
            ) : (
              dashboards.map((dashboard) => {
                const isSelected =
                  selectedView === "dashboard" &&
                  selectedDashboard === dashboard.id;

                return (
                  <Tooltip
                    key={dashboard.id}
                    title={
                      dashboard.tile_count === 1
                        ? i18n._(t`${dashboard.tile_count} tile`)
                        : i18n._(t`${dashboard.tile_count} tiles`)
                    }
                    arrow
                    placement="right"
                  >
                    <Box
                      onClick={() => handleSelectDashboard(dashboard.id)}
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        minHeight: "34px",
                        mb: "6px",
                        pl: "6px",
                        pr: "2px",
                        borderRadius: "10px",
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? "rgba(255, 255, 255, 0.18)"
                          : "transparent",
                        transition:
                          "background-color 0.15s ease, opacity 0.15s ease",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "rgba(255, 255, 255, 0.18)"
                            : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "-18px",
                            top: "6px",
                            bottom: "6px",
                            width: "4px",
                            borderRadius: "999px",
                            backgroundColor: "rgba(17, 17, 17, 0.72)",
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: "0.95rem",
                          lineHeight: 1.2,
                          fontWeight: isSelected ? 700 : 400,
                          color: "rgba(17, 17, 17, 0.88)",
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {dashboard.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenMenu(event, dashboard)}
                        sx={{
                          ml: 0.5,
                          flexShrink: 0,
                          color: "rgba(17, 17, 17, 0.62)",
                          opacity: isSelected ? 1 : 0.72,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.22)",
                            color: "rgba(17, 17, 17, 0.84)",
                          },
                        }}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                );
              })
            )}
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleOpenRename}>{i18n._(t`Rename`)}</MenuItem>
        <MenuItem onClick={handleOpenDelete}>{i18n._(t`Delete`)}</MenuItem>
      </Menu>

      <Dialog
        open={renameOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(t`Rename dashboard`)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={i18n._(t`Dashboard title`)}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(t`Cancel`)}</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={renameValue.trim() === ""}
          >
            {i18n._(t`Save`)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(t`Delete dashboard?`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {menuDashboard
              ? i18n._(
                  t`This will remove "${menuDashboard.title}" and all of its tiles.`,
                )
              : i18n._(
                  t`This will remove this dashboard and all of its tiles.`,
                )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(t`Cancel`)}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            {i18n._(t`Delete`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardDrawerSection;
